import { isRestaurantNotificationType } from "@/lib/firebase/notification-types";

export type RestaurantNotificationClickMessage = {
  type: "restaurant-notification-click";
  clickId: string;
  data: Record<string, string>;
};

type Listener = (message: RestaurantNotificationClickMessage) => void;

const listeners = new Set<Listener>();
const pending: RestaurantNotificationClickMessage[] = [];
const handledClickIds = new Set<string>();
let installed = false;

function acceptMessage(value: unknown): void {
  if (!value || typeof value !== "object") return;
  const message = value as Partial<RestaurantNotificationClickMessage>;
  if (
    message.type !== "restaurant-notification-click" ||
    typeof message.clickId !== "string" ||
    !message.data ||
    !isRestaurantNotificationType(message.data.type) ||
    handledClickIds.has(message.clickId)
  ) {
    return;
  }
  handledClickIds.add(message.clickId);
  const trusted: RestaurantNotificationClickMessage = {
    type: message.type,
    clickId: message.clickId,
    data: message.data,
  };
  if (listeners.size === 0) pending.push(trusted);
  else listeners.forEach((listener) => listener(trusted));
}

function handleServiceWorkerMessage(
  value: unknown,
  source?: Pick<ServiceWorker, "postMessage"> | null,
): void {
  if (
    value &&
    typeof value === "object" &&
    (value as { type?: unknown }).type ===
      "restaurant-notification-client-ready-request"
  ) {
    source?.postMessage({ type: "restaurant-notification-client-ready" });
    return;
  }
  acceptMessage(value);
}

/**
 * Install before React boots, so a service worker opening a cold window cannot
 * outrun the authenticated notification runtime. Messages wait in memory until
 * ProtectedLayout has established the restaurant session.
 */
export function installRestaurantNotificationClickBridge(): void {
  if (installed || typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
    return;
  }
  installed = true;
  navigator.serviceWorker.addEventListener("message", (event) => {
    handleServiceWorkerMessage(
      event.data,
      event.source as ServiceWorker | null,
    );
  });
  void navigator.serviceWorker.ready
    .then((registration) => {
      const worker = navigator.serviceWorker.controller ?? registration.active;
      worker?.postMessage({ type: "restaurant-notification-client-ready" });
    })
    .catch((error) => {
      console.warn(
        "[restaurant-notifications] Service-worker click bridge unavailable",
        error,
      );
    });
}

export function subscribeToRestaurantNotificationClicks(
  listener: Listener,
): () => void {
  listeners.add(listener);
  const queued = pending.splice(0, pending.length);
  for (const message of queued) listener(message);
  return () => listeners.delete(listener);
}

/** Test seam for the browser's service-worker message event. */
export function receiveRestaurantNotificationClickForTest(value: unknown): void {
  handleServiceWorkerMessage(value);
}

/** Test seam for a readiness request from a restarted service worker. */
export function receiveRestaurantNotificationReadyRequestForTest(
  source: Pick<ServiceWorker, "postMessage">,
): void {
  handleServiceWorkerMessage(
    { type: "restaurant-notification-client-ready-request" },
    source,
  );
}
